import { motion } from 'framer-motion';
import { ArrowRight, Play, FileText, Sparkles, Braces } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Arrow connector component
const ArrowConnector = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.4, delay: 0.5 }}
    className="flex items-center justify-center"
  >
    <div className="flex items-center">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: 32 }}
        transition={{ duration: 0.4, delay: 0.5 }}
        className="h-0.5 bg-gradient-to-r from-[#635BFF]/30 to-[#635BFF]"
      />
      <motion.div
        animate={{ x: [0, 4, 0] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
        className="mx-1"
      >
        <ArrowRight className="w-5 h-5 text-[#635BFF]" />
      </motion.div>
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: 32 }}
        transition={{ duration: 0.4, delay: 0.6 }}
        className="h-0.5 bg-gradient-to-r from-[#635BFF] to-[#635BFF]/30"
      />
    </div>
  </motion.div>
);

// Mobile arrow (vertical)
const MobileArrow = ({ delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.4, delay: delay }}
    className="flex justify-center py-3"
  >
    <motion.div
      animate={{ y: [0, 6, 0] }}
      transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
    >
      <ArrowRight className="w-5 h-5 text-[#635BFF] rotate-90" />
    </motion.div>
  </motion.div>
);

// Document card component
const DocumentCard = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 0.3 }}
    className="flex-shrink-0"
  >
    <motion.div
      animate={{ y: [0, -6, 0] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      className="bg-white rounded-2xl shadow-xl p-5 w-44 border border-gray-100 relative overflow-hidden"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 bg-[#F7F8FC] rounded-lg flex items-center justify-center">
          <FileText className="w-4 h-4 text-[#635BFF]" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[#0A2540]">Invoice</p>
          <p className="text-xs text-[#969DA3]">PDF</p>
        </div>
      </div>
      <div className="space-y-2">
        <div className="h-2 bg-[#F7F8FC] rounded-full w-full" />
        <div className="h-2 bg-[#F7F8FC] rounded-full w-4/5" />
        <div className="h-2 bg-[#F7F8FC] rounded-full w-3/5" />
        <div className="mt-3 flex gap-2">
          <div className="h-5 bg-[#F7F8FC] rounded w-14" />
          <div className="h-5 bg-[#635BFF]/10 rounded w-16" />
        </div>
      </div>
      {/* Scan line */}
      <motion.div
        animate={{ y: [-10, 120, -10] }}
        transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute left-4 right-4 h-0.5 bg-gradient-to-r from-transparent via-[#635BFF] to-transparent"
      />
    </motion.div>
  </motion.div>
);

// AI Processor component
const AIProcessor = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5, delay: 0.5 }}
    className="flex-shrink-0 relative"
  >
    <motion.div
      animate={{ scale: [1, 1.03, 1] }}
      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      className="w-20 h-20 bg-gradient-to-br from-[#635BFF] to-[#7C3AED] rounded-2xl flex items-center justify-center shadow-lg shadow-[#635BFF]/25"
    >
      <Sparkles className="w-9 h-9 text-white" />
    </motion.div>
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
      className="absolute -inset-2 border-2 border-dashed border-[#635BFF]/20 rounded-2xl pointer-events-none"
    />
    <motion.div
      animate={{ opacity: [0.3, 1, 0.3] }}
      transition={{ duration: 1.5, repeat: Infinity }}
      className="absolute -top-1 -right-1 w-3 h-3 bg-[#00D4FF] rounded-full"
    />
  </motion.div>
);

// JSON Output component
const JSONOutput = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 0.7 }}
    className="flex-shrink-0"
  >
    <motion.div
      animate={{ y: [0, -6, 0] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
      className="bg-[#1E1E3F] rounded-2xl shadow-xl p-4 w-52 font-mono text-xs"
    >
      <div className="flex items-center gap-1.5 mb-3 pb-2 border-b border-white/10">
        <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
        <span className="ml-2 text-[10px] text-white/40">output.json</span>
      </div>
      <pre className="text-[11px] leading-relaxed">
        <span className="text-gray-400">{'{'}</span>{'\n'}
        <span className="text-[#89DDFF]">  "vendor"</span><span className="text-white">: </span><span className="text-[#C3E88D]">"Acme"</span><span className="text-white">,</span>{'\n'}
        <span className="text-[#89DDFF]">  "total"</span><span className="text-white">: </span><span className="text-[#F78C6C]">1250.00</span><span className="text-white">,</span>{'\n'}
        <span className="text-[#89DDFF]">  "date"</span><span className="text-white">: </span><span className="text-[#C3E88D]">"2024-01"</span>{'\n'}
        <span className="text-gray-400">{'}'}</span>
      </pre>
      <div className="mt-2 flex items-center gap-1.5">
        <Braces className="w-3.5 h-3.5 text-[#00D4FF]" />
        <span className="text-[10px] text-[#00D4FF]">Valid JSON</span>
      </div>
    </motion.div>
  </motion.div>
);

// Pipeline illustration - Desktop horizontal layout
const PipelineIllustration = () => (
  <div className="hidden lg:flex items-center justify-center gap-8 xl:gap-10">
    <DocumentCard />
    <ArrowConnector />
    <AIProcessor />
    <ArrowConnector />
    <JSONOutput />
  </div>
);

// Pipeline illustration - Mobile/Tablet vertical layout
const PipelineIllustrationMobile = () => (
  <div className="flex lg:hidden flex-col items-center">
    <DocumentCard />
    <MobileArrow delay={0.4} />
    <AIProcessor />
    <MobileArrow delay={0.6} />
    <JSONOutput />
  </div>
);

export const HeroSection = () => {
  return (
    <section className="relative pt-24 md:pt-28 pb-16 overflow-hidden" data-testid="hero-section">
      {/* Background effects */}
      <div className="absolute inset-0 gradient-hero pointer-events-none" />
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-[#635BFF]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#00D4FF]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Text content - centered on all screens */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-12 lg:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#635BFF]/10 rounded-full mb-6"
          >
            <Sparkles className="w-4 h-4 text-[#635BFF]" />
            <span className="text-sm font-medium text-[#635BFF]">AI-Powered Document Processing</span>
          </motion.div>

          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A2540] leading-[1.1] tracking-tight mb-6">
            Extract Data From{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#635BFF] to-[#7C3AED]">
              Documents
            </span>{' '}
            Instantly
          </h1>

          <p className="text-lg md:text-xl text-[#425466] leading-relaxed mb-8 max-w-2xl mx-auto">
            Upload invoices, receipts, or PDFs and get clean structured data automatically using AI-powered OCR.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center relative z-10">
            <Button
              size="lg"
              className="bg-[#635BFF] hover:bg-[#5851E0] text-white rounded-full px-8 h-14 text-base font-semibold transition-all duration-200 active:scale-95 shadow-lg shadow-[#635BFF]/25"
              data-testid="hero-start-btn"
            >
              Start Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <a href="https://docs.smartocr.my.id/docs/intro/" target="_blank" rel="noopener noreferrer">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8 h-14 text-base font-semibold border-2 border-gray-200 hover:border-[#635BFF] hover:text-[#635BFF] transition-all duration-200 active:scale-95"
                data-testid="hero-docs-btn"
              >
                <Play className="w-5 h-5 mr-2" />
                View Documentation
              </Button>
            </a>
          </div>
        </motion.div>

        {/* Pipeline Illustration - Properly spaced below text */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-12 lg:mb-16"
        >
          <PipelineIllustration />
          <PipelineIllustrationMobile />
        </motion.div>

        {/* Trust indicators - centered */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className="text-center"
        >
          <p className="text-sm text-[#969DA3] mb-4">Trusted by developers worldwide</p>
          <div className="flex items-center justify-center gap-6 sm:gap-10">
            <div className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-[#0A2540]">10M+</p>
              <p className="text-xs sm:text-sm text-[#969DA3]">Documents Processed</p>
            </div>
            <div className="w-px h-10 bg-gray-200" />
            <div className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-[#0A2540]">99.9%</p>
              <p className="text-xs sm:text-sm text-[#969DA3]">Accuracy Rate</p>
            </div>
            <div className="w-px h-10 bg-gray-200" />
            <div className="text-center">
              <p className="text-2xl sm:text-3xl font-bold text-[#0A2540]">&lt;2s</p>
              <p className="text-xs sm:text-sm text-[#969DA3]">Avg Processing</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
